import React from 'react';
import {Result} from 'templates/reports/query';
import {DexaMetric, Section} from 'templates/reports/types';
import {useHistorical} from 'templates/reports/useHistorical';
import {useSnapshot} from 'templates/reports/useSnapshot';

import DataTable from './DataTable';
import Historical from './Historical';
import Percentiles from './Percentiles';
import {RegionalComp} from './RegionalComp';
import {RmrEstimate} from './RmrEstimate';
import ScanImages from './ScanImages';
import SectionContent from './SectionContent';
import Snapshot from './Snapshot';

export const factory = (data: Result, s: Section) => {
	const report = data.reports;
	if (!report || !report.dexa) {
		return null;
	}

	const section = report.dexa.sections
		.find((v) => v.metric === s.metric);

	if (!section || !section.historicals.length) {
		switch (s.details.footer) {
		case 'regional-comp': {
			return !section?.results?.length ? null : (
				<RegionalComp regional={section} />
			);
		}

		case 'rmr-estimate': {
			return !section ? null : (
				<RmrEstimate cfg={s.details.historical} rmr={section} />
			);
		}

		case 'scan-images': {
			if (report.dexa.images && report.dexa.images.length > 0) {
				return <ScanImages data={report.dexa.images} />;
			}
			return null;
		}}

		return (
			<>
				{s.details.illustration}
				{s.details.footer}
			</>
		);
	}

	const {snapshot} = useSnapshot(section, s.details.snapshot.chart, s.reverse);
	const {historical} = useHistorical(section, s.details.historical.chart, s.reverse);

	if (!section.results.length) {
		if (section.metric === DexaMetric.VisceralFat) {
			return (
				<div>
					Visceral Adipose Tissue measurements are not
					included in pediatric scans.
				</div>
			);
		}
		return null;
	}

	return (
		<SectionContent>
			{s.details.illustration}
			<Snapshot
				chart={snapshot}
				data={section}
				cfgs={s.details.snapshot}
			>
				<Percentiles
					breaks={section.historicals[0].breakpoints}
					label={s.details.percentiles.label}
					reverse={s.reverse}
				/>
			</Snapshot>
			<Historical
				chart={historical}
				cfgs={s.details.historical}
			>
				<Percentiles
					breaks={section.historicals[0].breakpoints}
					label={s.details.percentiles.label}
					reverse={s.reverse}
				/>
			</Historical>
			<DataTable
				data={section}
				cfgs={s.details.table}
				reverse={s.reverse}
			/>
			{s.details.footer}
		</SectionContent>
	);
};
